import { truncateAddress } from '@kwenta/sdk/utils'
import { FC, memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import DelegationIcon from 'assets/svg/futures/delegation-icon.svg'
import BaseModal from 'components/BaseModal'
import Button from 'components/Button'
import { FlexDivRowCentered } from 'components/layout/flex'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectSelectedDelegationAddress } from 'state/futures/selectors'
import { removeDelegateForSMAccount } from 'state/futures/snxPerpsV2/actions'
import { removeDelegateForCMAccount } from 'state/futures/snxPerpsV3/actions'
import { ManageModalType, PerpsProvider } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { DelegationSubTab } from './types'

type Props = {
	onDismiss(): void
}

const RemoveDelegateModal: FC<Props> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const selectedAddress = useAppSelector(selectSelectedDelegationAddress)
	const perpsProvider = useAppSelector(selectPerpsProvider)

	const onRemove = useCallback(() => {
		const params = { tab: ManageModalType.Delegation, subTab: DelegationSubTab.Delegations }
		if (perpsProvider === PerpsProvider.SNX_V2) {
			dispatch(removeDelegateForSMAccount())
			dispatch(
				setOpenModalWithParams({
					type: 'futures_deposit_withdraw_smart_margin',
					params,
				})
			)
		} else {
			dispatch(removeDelegateForCMAccount())
			dispatch(
				setOpenModalWithParams({
					type: 'futures_deposit_withdraw_cross_margin',
					params,
				})
			)
		}
	}, [perpsProvider, dispatch])

	return (
		<StyledBaseModal
			title={t('futures.market.trade.margin.modal.delegation.remove.modal-title')}
			isOpen
			onDismiss={onDismiss}
		>
			<Spacer height={30} />
			<Body size="small" color="secondary">
				{t('futures.market.trade.margin.modal.delegation.remove.copy')}
			</Body>
			<Spacer height={20} />
			<InputContainer>
				<FlexDivRowCentered justifyContent="flexStart" columnGap="12px">
					<DelegationIcon />
					<Body color="preview">{truncateAddress(selectedAddress)}</Body>
				</FlexDivRowCentered>
			</InputContainer>
			<Spacer height={30} />
			<StyledButton size="small" fullWidth onClick={onRemove}>
				{t('futures.market.trade.margin.modal.delegation.remove.remove-button')}
			</StyledButton>
		</StyledBaseModal>
	)
})

const StyledButton = styled(Button)`
	color: ${(props) => props.theme.colors.selectedTheme.button.red.text};
	border-width: 0px;
	background: ${(props) => props.theme.colors.selectedTheme.button.red.fill};
	cursor: pointer;
`

const InputContainer = styled(FlexDivRowCentered)`
	justify-content: flex-start;
	width: 100%;
	border-radius: 8px;
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.pill.gray.outline.border};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.primary.background};
	padding: 10px;
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 400px;
		margin-top: 300px;
	}
`

export default RemoveDelegateModal
